const HOSTNAME =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

const hostnameOf = (host: string | undefined): string | null => {
  if (host === undefined) return null;
  const withoutPort = host.split(":")[0];
  if (withoutPort === undefined) return null;
  const lowered = withoutPort.trim().toLowerCase();
  if (lowered === "" || lowered.length > 253 || !HOSTNAME.test(lowered)) return null;
  return lowered;
};

export type Origin = {
  readonly origin: string;
  readonly protocol: string;
  readonly hostname: string;
};

export const originOf = (value: string | undefined): Origin | null => {
  if (value === undefined || value.trim() === "") return null;
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  const hostname = hostnameOf(url.hostname);
  if (hostname === null) return null;
  if (url.username !== "" || url.password !== "") return null;
  if (url.origin !== value.trim().replace(/\/+$/, "")) return null;
  return { origin: url.origin, protocol: url.protocol, hostname };
};

class InvalidOriginError extends Error {
  constructor(variable: string, value: string) {
    super(`${variable} carries ${JSON.stringify(value)}, which is not a scheme-and-host origin`);
    this.name = "InvalidOriginError";
  }
}

export const parseOriginList = (variable: string, raw: string): readonly string[] =>
  raw
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "")
    .map((entry) => {
      const parsed = originOf(entry);
      if (parsed === null) throw new InvalidOriginError(variable, entry);
      return parsed.origin;
    });

export const allowedOrigin = (
  value: string | undefined,
  allowed: readonly string[],
): string | null => {
  const parsed = originOf(value);
  return parsed !== null && allowed.includes(parsed.origin) ? parsed.origin : null;
};

export const originAllowed = (value: string | undefined, allowed: readonly string[]): boolean =>
  allowedOrigin(value, allowed) !== null;

const RETURN_BASE = "https://return.invalid";

const leavesThePath = (value: string): boolean =>
  /[\\\s]/.test(value) ||
  [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code < 0x20 || code === 0x7f;
  });

export const returnPathAllowed = (value: string | undefined): boolean => {
  if (value === undefined || !value.startsWith("/") || leavesThePath(value)) return false;
  try {
    return new URL(value, RETURN_BASE).origin === RETURN_BASE;
  } catch {
    return false;
  }
};
